import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { WarningMessage } from '@eika/attention-message';
import { ButtonTypes, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import { ArbeidssituasjonWeb, InntektKildeWeb, InntektWeb, StatusWeb, ValgtWeb } from '../../generated-types';
import { useDeleteInntekt, useInntekt, useUpdateInntekt } from '../../hooks/useInntekt';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import InntektKunde from './inntektKunde';
import InntektSbl from './inntektSbl';
import InntektValg from './inntektValg';

const newInntekt = {
    inntektId: null,
    arbeidsgiver: undefined,
    arbeidssituasjon: ArbeidssituasjonWeb.IKKE_VALGT,
    aarsinntektKunde: null,
    aarsinntektSbl: 0,
    harSblInntekt: ValgtWeb.JA,
    kilde: InntektKildeWeb.KUNDE,
};

export type InntektFormData = {
    harInntekt: ValgtWeb;
    inntekt: {
        inntektId: string | null;
        arbeidsgiver: string | undefined;
        arbeidssituasjon: ArbeidssituasjonWeb | undefined;
        aarsinntektKunde: number | null;
        aarsinntektSbl: number;
        harSblInntekt: ValgtWeb;
        kilde: InntektKildeWeb | null;
    }[];
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const InntektPage: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { inntekt, button } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: inntektContainer, isLoading } = useInntekt(soeknadstype);
    const aarsinntekt = inntektContainer?.inntekt;
    const harSblInntekt = aarsinntekt?.some((i) => i.kilde === InntektKildeWeb.SBL) || false;
    const methods = useForm<InntektFormData>({ defaultValues: { harInntekt: ValgtWeb.IKKE_VALGT, inntekt: [] } });
    const {
        register,
        setValue,
        control,
        reset,
        handleSubmit,
        formState: { errors },
    } = methods;
    const { fields, append, remove, swap } = useFieldArray({ name: 'inntekt', control });
    const { mutate: updateInntekt, isPending: isUpdating } = useUpdateInntekt(inntektContainer, () => {
        navigate(navigateTo);
    });
    const { mutate: deleteInntekt } = useDeleteInntekt();

    useEffect((): void => {
        if (aarsinntekt) {
            const inntektList: InntektFormData = { harInntekt: inntektContainer?.harInntekt, inntekt: [] };
            aarsinntekt.forEach((i) => {
                inntektList.inntekt.push({
                    inntektId: i.id,
                    arbeidsgiver: i.arbeidsgiver || '',
                    arbeidssituasjon: i.arbeidssituasjonWeb,
                    aarsinntektKunde: i.aarsinntektKunde,
                    aarsinntektSbl:
                        i.aarsinntektKunde && i.aarsinntektKunde > 0 ? i.aarsinntektKunde : i.aarsinntektSbl,
                    harSblInntekt: i.naavaerende ? ValgtWeb.JA : ValgtWeb.NEI,
                    kilde: i.kilde,
                });
            });
            reset(inntektList);
        }
    }, [aarsinntekt, inntektContainer, reset]);

    const onSumbitInntekt = (data: InntektFormData) => {
        if (inntektContainer?.soeknadId) {
            const inntektList: InntektWeb[] = [];
            data.inntekt.forEach((i1) => {
                const inntekt = inntektContainer.inntekt.find((i2) => i2.id === i1.inntektId);
                if (inntekt) {
                    if (inntekt.kilde === InntektKildeWeb.SBL) {
                        inntekt.aarsinntektFraKunde = inntekt.aarsinntektSbl !== i1.aarsinntektSbl;
                        inntekt.arbeidssituasjonWeb = i1.arbeidssituasjon || ArbeidssituasjonWeb.IKKE_VALGT;
                        inntekt.aarsinntekt = i1.aarsinntektSbl || i1?.aarsinntektKunde || 0;
                        inntekt.aarsinntektKunde =
                            inntekt.aarsinntektSbl !== i1.aarsinntektSbl ? i1.aarsinntektSbl : null;
                        inntekt.naavaerende = i1.harSblInntekt === ValgtWeb.JA;
                        inntekt.status = StatusWeb.CONFIRMED;
                    } else {
                        inntekt.arbeidsgiver = i1.arbeidsgiver || '';
                        inntekt.arbeidssituasjonWeb = i1.arbeidssituasjon || ArbeidssituasjonWeb.IKKE_VALGT;
                        inntekt.aarsinntekt = i1?.aarsinntektKunde || 0;
                        inntekt.aarsinntektKunde = i1?.aarsinntektKunde || 0;
                        inntekt.status = StatusWeb.CONFIRMED;
                    }
                    inntektList.push(inntekt);
                } else {
                    inntektList.push({
                        id: i1.inntektId,
                        arbeidsgiver: i1.arbeidsgiver || '',
                        arbeidssituasjonWeb: i1.arbeidssituasjon || ArbeidssituasjonWeb.IKKE_VALGT,
                        aarsinntektKunde: i1.aarsinntektKunde || null,
                        aarsinntektSbl: 0,
                        aarsinntekt: i1.aarsinntektKunde || 0,
                        kilde: i1.kilde,
                        aarsinntektFraKunde: true,
                        naavaerende: i1.harSblInntekt === ValgtWeb.JA,
                        status: StatusWeb.CONFIRMED,
                        organisasjonsnummer: null,
                    });
                }
            });
            updateInntekt({
                soeknadId: inntektContainer.soeknadId,
                harInntekt: data.harInntekt,
                inntekt: inntektList,
            });
        } else {
            navigate(navigateTo);
        }
    };

    const onClickHarInntekt = (valg: ValgtWeb) => {
        if (valg === ValgtWeb.JA && fields.length === 0) {
            append(newInntekt);
        } else if (valg === ValgtWeb.NEI) {
            const inntektIder: string[] = [];
            fields.forEach((f) => {
                if (f.inntektId !== null) {
                    inntektIder.push(f.inntektId);
                }
            });
            if (inntektContainer?.soeknadId && inntektIder.length > 0) {
                deleteInntekt(
                    { soeknadId: inntektContainer?.soeknadId, inntektIder },
                    {
                        onSuccess: () => {
                            remove();
                        },
                    },
                );
            } else {
                remove();
            }
        }
    };

    const onClickLeggTilInntekt = () => {
        append(newInntekt);
    };

    const onClickSlett = (index: number) => {
        const id = fields[index].inntektId;
        if (fields.length === 1) {
            setValue('harInntekt', ValgtWeb.NEI, { shouldValidate: true });
        }
        if (inntektContainer?.soeknadId && id) {
            deleteInntekt(
                { soeknadId: inntektContainer?.soeknadId, inntektIder: [id] },
                {
                    onSuccess: () => {
                        if (index === 0 && fields.length === 2) {
                            swap(0, 1);
                            remove(index);
                        } else {
                            remove(index);
                        }
                    },
                },
            );
        } else if (index === 0 && fields.length === 2) {
            swap(0, 1);
            remove(1);
        } else {
            remove(index);
        }
    };

    const onForrigeClick = () => {
        navigate(navigateFrom);
    };

    if (isLoading) return <Loader className="padding-top-extra-large" />;

    return (
        <PageCard title={inntekt.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSumbitInntekt)}>
                    <div className="row row-gap-large">
                        <div className="row row-gap-large">
                            <span className="typography-ingress__light">{inntekt.subTitle}</span>
                            {inntektContainer?.sblInntektFeilet && (
                                <WarningMessage
                                    id="samtykkeWarning"
                                    text={inntekt.infoSblError}
                                    icon={IconTypes.UTROPSTEGN_24}
                                />
                            )}
                        </div>
                        {!harSblInntekt && <InntektValg onClickHarInntekt={onClickHarInntekt} />}
                        <div>
                            {fields.map((field, index) => {
                                return (
                                    <div key={field.id} className="form-box-seagreen row-gap margin-bottom-small">
                                        {field.kilde === InntektKildeWeb.SBL && (
                                            <InntektSbl
                                                index={index}
                                                register={register}
                                                control={control}
                                                setValue={setValue}
                                                errors={errors}
                                            />
                                        )}
                                        {field.kilde === InntektKildeWeb.KUNDE && (
                                            <InntektKunde
                                                index={index}
                                                register={register}
                                                control={control}
                                                setValue={setValue}
                                                errors={errors}
                                                onClickSlett={onClickSlett}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {fields.length > 0 && (
                                <div className="column">
                                    <StandardButton
                                        icon={IconTypes.PLUSS_36}
                                        buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                        onClick={onClickLeggTilInntekt}
                                    >
                                        {button.leggTilArbeidsinntekt}
                                    </StandardButton>
                                </div>
                            )}
                        </div>
                        <PageFooter
                            soeknadId={inntektContainer?.soeknadId}
                            showSpinner={isUpdating}
                            onForrigeClick={onForrigeClick}
                        />
                    </div>
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default InntektPage;
